/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-alert */
import { useContext, useState } from 'react';
import axios from 'axios';
import AppStateContext from '../app/context/appStateContext';
import SysGetUsers from "../functions/SysGetUsers";

import APIHelper from "./useAPIHelper";

const LOGIN_URL = '/.netlify/functions/login';

/**
 * The logo map and the user's entitlement now arrive ON THE LOGIN RESPONSE. PORTAL-159.
 *
 * They used to be two further calls, to getCustomerDetail and getCustomerContractDetails, made from
 * here immediately after the login succeeded. Both were unauthenticated: one took a username from
 * the request body and returned that person's role, permissions and contracts to anyone who asked,
 * the other took no input and returned every customer the portal held. Resolving them inside login,
 * where Pronto has just verified the identity, let both endpoints be deleted rather than guarded.
 *
 * The two functions below are now pure - they shape data already in hand and make no request - so
 * they no longer need apiRunner, which exists to classify HTTP failures. That is also why the login
 * journey lost about 500 ms: two Netlify invocations, each carrying roughly 250 ms of platform cost
 * before doing any work.
 */

const LoginRequest = (callback: any) => {
  const { defaultNumber, setAppStateDetails } = useContext(AppStateContext);
  const apiRunner = APIHelper();

  const [inputs, setInputs] = useState({ userName: '', password: '' });

  function initialiseLogos(cmsResponse: any[]) {
    if (cmsResponse) {
      // store all logos
      const customerMap: { [index: string]: any } = {
        default: {
          logo: '',
          number: '',
        }
      };

      cmsResponse.map((customer: any, index: number) => {
        let customerInfo: {logo: any, number: string} = {};
        customerInfo.logo = customer.data.logo.url != ''? customer.data.logo.url: '';
        customerInfo.number = customer.data.phone_number != null? customer.data.phone_number: defaultNumber;
        customerMap[customer.data.customer_id] = customerInfo;
      });
      // store map in context
      setAppStateDetails({ customerInfoMap: customerMap });
    }
  }

  function initialiseUserInfo(userInfo: any) {
    // login returns null when the account authenticated to Pronto but has no pronto_user record in
    // Prismic. That was already the effective outcome before this change - the separate call
    // returned an empty body and the page rendered with no permissions - so the default below
    // preserves it rather than inventing a new failure. Fixing it is its own ticket.
    const cmsResponse = userInfo || {
      contractlist: [],
      roles1: {
        data: {
          permissions: [],
        }
      },
    };

    if (cmsResponse) {
      // store all roles
      const roleArray: any[] = [];
      let contractList: {} = {};

      cmsResponse.roles1.data.permissions.map((permission: any) => {
        roleArray.push(permission.permission.uid);
        return null;
      });

      cmsResponse.contractlist.map((contract: any) => {
        contractList[contract.customercode1] = contract.contractno1[0].text
          .split(',')
          .map((item, index) => {
            return item.replace(/\s/g,'');
          });
        return null;
      });

      // store map in context
      setAppStateDetails({ userPermissionsList: roleArray, contractList: contractList });
    }
  }

  const getUserRealName = async (userName: string, token: string) => {
    let name: string = '';

    const nameCallback = (data: string) => {
      name = data;
    };

    await apiRunner.run(SysGetUsers, nameCallback,[userName, token]);

    setAppStateDetails({
      name: name,
    })
  };

  const fetchData = async () => {
    try {
      const response = await axios({
        method: 'POST',
        url: LOGIN_URL,
        headers: {
          'X-Pronto-Username': inputs.userName,
          'X-Pronto-Password': inputs.password,
        },
      });

      if (response.status === 200) {
        if (response.data.AuthInfo.token) {
          setAppStateDetails({ userId: inputs.userName, token: response.data.AuthInfo.token });
          initialiseLogos(response.data.customers);
          initialiseUserInfo(response.data.userInfo);
          await getUserRealName(inputs.userName, response.data.AuthInfo.token);
          return true;
        }
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const handleSubmit = async (event: any) => {
    if (event) {
      event.preventDefault();
      const res = await fetchData();
      callback(res);
    }
  };

  const handleInputChange = (event: any) => {
    event.persist();
    setInputs(inputChange => ({ ...inputChange, [event.target.name]: event.target.value }));
  };

  return {
    handleSubmit,
    handleInputChange,
    inputs,
  };
};

export default LoginRequest;
